/**
 * The EditorDocs port for the web. This module is in the main bundle but imports nothing from Loro: `open()` returns
 * a proxy right away and loads the docs chunk (loro-crdt WASM, loro-websocket, the adapter) on first use.
 */
import type { ApiClient, EditorDoc, EditorDocState, EditorDocs, EditorUser, NetworkMonitor } from "@bundle/core";

export type EditorDocsDeps = { api: ApiClient; network: NetworkMonitor; mock: boolean; namespace: string };

const LOADING: EditorDocState = { snapshot: { blocks: {}, roots: [] }, ready: false, unavailable: false, sync: "connecting", peers: [], canUndo: false, canRedo: false };
const FAILED: EditorDocState = { ...LOADING, unavailable: true, sync: "offline" };

/** Stands in for the real doc until the chunk has loaded. */
class LazyEditorDoc implements EditorDoc {
  private inner: EditorDoc | undefined;
  private state: EditorDocState = LOADING;
  private readonly listeners = new Set<() => void>();
  private disposed = false;

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = (): EditorDocState => this.inner?.getSnapshot() ?? this.state;

  private notify(): void {
    for (const l of Array.from(this.listeners)) l();
  }

  attach(doc: EditorDoc): void {
    if (this.disposed) {
      doc.dispose();
      return;
    }
    this.inner = doc;
    doc.subscribe(() => this.notify());
    this.notify();
  }

  fail(): void {
    this.state = FAILED;
    this.notify();
  }

  apply: EditorDoc["apply"] = (ops, caret) => this.inner?.apply(ops, caret);
  undo: EditorDoc["undo"] = () => this.inner?.undo();
  redo: EditorDoc["redo"] = () => this.inner?.redo();
  setCaret: EditorDoc["setCaret"] = (c) => this.inner?.setCaret(c);

  dispose(): void {
    this.disposed = true;
    this.inner?.dispose();
    this.listeners.clear();
  }
}

export function createEditorDocs(deps: EditorDocsDeps): EditorDocs {
  let chunk: Promise<{ open: (ws: string, pageId: string, me: EditorUser) => Promise<EditorDoc> }> | undefined;
  const load = () =>
    (chunk ??= (async () => {
      const [{ openPageDoc }, connector] = await Promise.all([
        import("./pageDoc.ts"),
        deps.mock ? import("../mock/pageHub.ts").then((m) => m.mockConnector()) : import("./websocket.ts").then((m) => m.websocketConnector(deps.api)),
      ]);
      const docDeps = { api: deps.api, network: deps.network, connector, namespace: deps.namespace };
      return { open: (ws: string, pageId: string, me: EditorUser) => openPageDoc(docDeps, ws, pageId, me) };
    })());
  return {
    open(ws, pageId, me) {
      const proxy = new LazyEditorDoc();
      load()
        .then((m) => m.open(ws, pageId, me))
        .then((doc) => proxy.attach(doc))
        .catch((e: unknown) => {
          console.error("[docs] couldn't open the page body", e);
          chunk = undefined;
          proxy.fail();
        });
      return proxy;
    },
  };
}
